/**
 * Meeting lifecycle phase. Lives in its own file so both `meeting.ts` and
 * `streaming.ts` can depend on it without importing each other (which created
 * a cyclic shared/ types subgraph after consolidation).
 *
 * `MEETING_STATUSES` is the runtime const companion to `TMeetingStatus` —
 * use it any time code wants to compare against a status string, build a
 * "live phases" set, or anywhere else a literal would otherwise creep in.
 * Status renames then become a single-file edit caught at compile time on
 * every consumer.
 */
export const MEETING_STATUSES = {
	DETECTED: "detected",
	IN_PROGRESS: "in_progress",
	RECOVERING: "recovering",
	ENDED: "ended",
	TRANSCRIBED: "transcribed",
	PARSED: "parsed",
	SUMMARIZED: "summarized",
	FETCHED: "fetched",
	ERROR: "error",
} as const;

export type TMeetingStatus = (typeof MEETING_STATUSES)[keyof typeof MEETING_STATUSES];

/**
 * Phases representing a meeting that's still being processed (anything
 * pre-summary). The UI uses this to gate "live" affordances like the
 * recording controls and the finalization progress bar.
 */
export const LIVE_MEETING_STATUSES: ReadonlySet<string> = new Set<TMeetingStatus>([
	MEETING_STATUSES.DETECTED,
	MEETING_STATUSES.IN_PROGRESS,
	MEETING_STATUSES.TRANSCRIBED,
	MEETING_STATUSES.PARSED,
]);

/**
 * Phases where the recording flow is still active and the user could
 * resume / pause / hit the recovery window. Subset of LIVE_MEETING_STATUSES
 * — `transcribed` and `parsed` have stopped recording even though they're
 * still being processed.
 */
export const RECORDING_MEETING_STATUSES: ReadonlySet<string> = new Set<TMeetingStatus>([
	MEETING_STATUSES.DETECTED,
	MEETING_STATUSES.IN_PROGRESS,
	MEETING_STATUSES.RECOVERING,
]);

/**
 * Post-recording processing phases — the finalize "wow" screen polls fast
 * through these. `recovering` is included: a finish that raced the clean stop
 * (or the orphan scanner) finalizes via the recovery path under that phase, and
 * the finalize surface needs to poll briskly to catch the summary landing.
 */
export const FINALIZING_MEETING_STATUSES: ReadonlySet<string> = new Set<TMeetingStatus>([
	MEETING_STATUSES.ENDED,
	MEETING_STATUSES.TRANSCRIBED,
	MEETING_STATUSES.PARSED,
	MEETING_STATUSES.RECOVERING,
]);
